import React from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import LoginPage from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { DevicesPage } from './pages/DevicesPage'
import HistoryPage from './pages/HistoryPage'
import './App.css'

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
})

interface User {
  id: string
  username: string
  role: string
}

declare global {
  interface ImportMeta {
    env: {
      VITE_API_BASE_URL: string;
    };
  }
}

function App() {
  // Initialize state from localStorage
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => {
    const saved = localStorage.getItem('isAuthenticated')
    return saved === 'true'
  })
  const [currentPage, setCurrentPage] = React.useState(() => {
    const saved = localStorage.getItem('currentPage')
    return saved || 'login'
  })
  const [user, setUser] = React.useState<User | null>(() => {
    const saved = localStorage.getItem('user')
    return saved ? JSON.parse(saved) : null
  })

  const handleLogin = async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json() as { success: boolean; user?: User; error?: string }

      if (data.success) {
        const userData = data.user || null
        setUser(userData)
        setIsAuthenticated(true)
        setCurrentPage('dashboard')
        
        // Save to localStorage
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('currentPage', 'dashboard')
        localStorage.setItem('user', JSON.stringify(userData))
      } else {
        alert('Login failed: ' + (data.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Login error:', error)
      alert('Login failed. Please try again.')
    }
  }

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear state and localStorage
      setIsAuthenticated(false)
      setUser(null)
      setCurrentPage('login')
      
      // Clear localStorage
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('currentPage')
      localStorage.removeItem('user')
    }
  }

  const handleNavigate = (page: string) => {
    setCurrentPage(page)
    localStorage.setItem('currentPage', page)
  }

  const renderPage = () => {
    if (!isAuthenticated) {
      return <LoginPage onLogin={handleLogin} />
    }

    switch (currentPage) {
      case 'dashboard':
        return (
          <DashboardPage
            user={user}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        )
      case 'devices':
        return (
          <DevicesPage
            user={user}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        )
      case 'history':
        return (
          <HistoryPage 
            user={user}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        )
      default:
        return <LoginPage onLogin={handleLogin} />
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        {renderPage()}
      </div>
    </ThemeProvider>
  )
}

export default App 